import {
  AssistantMessage,
  CONTENT_TYPES,
  Content,
  FunctionCall,
  Message,
  SystemMessage,
  TemplateFormat,
  ToolCall,
} from "@/types";

export interface TraceMessageBlock {
  id: string;
  displayMessage: Message;
  jsonValue: unknown;
}

export interface TraceTranscriptStats {
  turnCount: number;
  toolCallCount: number;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

// Content block types that always represent a built-in (server-side) tool call: OpenAI's code
// interpreter / MCP / shell / apply-patch, Google's code execution, or Anthropic's server-side
// tool use (web search, bash, text editor, code execution). These don't show up in
// `message.tool_calls` (that array is only for client-executed function calls), so they need
// to be counted separately.
//
// `output_media` is handled separately below: it's only a tool call when it came from a real
// `image_generation_call`, not from a model's native/inline image output (Gemini `inline_data`,
// OpenAI's inline `output_image` part, or the standalone Images API) -- see
// `isToolGeneratedOutputMedia`.
//
// Result-only block types (web_search_tool_result, code_execution_result, etc.) are
// intentionally NOT counted: the backend mappers always emit them alongside (Anthropic) or
// immediately after (OpenAI shell/apply_patch, separate message) their originating call block,
// so counting them too would double count. mcp_list_tools/mcp_approval_request/response are
// metadata/approval-flow items, not invocations, so they're excluded too.
const BUILT_IN_TOOL_CALL_CONTENT_TYPES = new Set<string>([
  CONTENT_TYPES.CODE,
  CONTENT_TYPES.MCP_CALL,
  CONTENT_TYPES.SHELL_CALL,
  CONTENT_TYPES.APPLY_PATCH_CALL,
  CONTENT_TYPES.SERVER_TOOL_USE,
]);

// Some built-in tools (OpenAI web_search/file_search; Google web_search/google_maps/
// file_search/url_context) have NO dedicated call content block at all -- the backend mappers
// drop the call item entirely and only preserve citations as annotations on the following
// text block. Map each countable annotation type to a "tool group" so multiple citations from
// the same search still only count as one tool call. `container_file_citation` is intentionally
// excluded: it cites a `code_interpreter` call's own output file, which is already counted via
// the `code` block above.
const ANNOTATION_TYPE_TO_TOOL_GROUP: Record<string, string> = {
  url_citation: "web_search",
  map_citation: "google_maps",
  file_citation: "file_search",
};

/**
 * `OutputMediaContent.id` is only ever set at the real `image_generation_call` site; native/
 * inline model image output (Gemini `inline_data`, OpenAI's inline `output_image` part) and
 * the standalone Images API never set it.
 */
const isToolGeneratedOutputMedia = (item: Content): boolean =>
  item.type === CONTENT_TYPES.OUTPUT_MEDIA && Boolean(item.id);

const countBuiltInToolCalls = (content: Content[] | undefined): number => {
  if (!content?.length) {
    return 0;
  }

  const serverToolUseIds = new Set<string | undefined>();
  const serverToolUseNames = new Set<string | undefined>();
  for (const item of content) {
    if (item.type === CONTENT_TYPES.SERVER_TOOL_USE) {
      serverToolUseIds.add(item.id);
      serverToolUseNames.add(item.name);
    }
  }

  let count = 0;
  const annotationToolGroupsSeen = new Set<string>();
  for (const item of content) {
    if (item.type === CONTENT_TYPES.OUTPUT_MEDIA) {
      if (isToolGeneratedOutputMedia(item)) {
        count += 1;
      }
      continue;
    }

    if (BUILT_IN_TOOL_CALL_CONTENT_TYPES.has(item.type)) {
      // Anthropic emits a companion `code` block (sharing the same id) alongside
      // `server_tool_use` for bash/text-editor code execution calls, purely for display. That
      // pair represents a single tool call and must only be counted once.
      if (item.type === CONTENT_TYPES.CODE && "id" in item && serverToolUseIds.has(item.id)) {
        continue;
      }
      count += 1;
      continue;
    }

    if (item.type === CONTENT_TYPES.TEXT) {
      for (const annotation of item.annotations ?? []) {
        const toolGroup = ANNOTATION_TYPE_TO_TOOL_GROUP[annotation.type];
        if (!toolGroup || annotationToolGroupsSeen.has(toolGroup)) {
          continue;
        }
        // Anthropic's web_search already produced a `server_tool_use` block, counted above.
        if (toolGroup === "web_search" && serverToolUseNames.has("web_search")) {
          continue;
        }
        annotationToolGroupsSeen.add(toolGroup);
        count += 1;
      }
    }
  }
  return count;
};

const normalizeMessageContent = (content: unknown): Content[] => {
  if (Array.isArray(content)) {
    return content as Content[];
  }

  if (typeof content === "string" && content.length > 0) {
    return [{ type: CONTENT_TYPES.TEXT, text: content }];
  }

  return [];
};

const normalizeFunctionCall = (value: unknown): FunctionCall | undefined => {
  if (!isRecord(value)) {
    return undefined;
  }

  const name = typeof value.name === "string" ? value.name : "";
  const argumentsValue = typeof value.arguments === "string" ? value.arguments : "";

  if (!name && !argumentsValue) {
    return undefined;
  }

  return {
    name,
    arguments: argumentsValue,
  };
};

const normalizeToolCalls = (value: unknown): ToolCall[] | undefined => {
  if (!Array.isArray(value)) {
    return undefined;
  }

  return value.filter(isRecord).map((toolCall, index) => {
    const functionCall = normalizeFunctionCall(toolCall.function);

    return {
      id:
        typeof toolCall.id === "string"
          ? toolCall.id
          : `tool-call-${typeof toolCall._id === "string" ? toolCall._id : index}`,
      type: "function",
      tool_id: typeof toolCall.tool_id === "string" ? toolCall.tool_id : undefined,
      _id: typeof toolCall._id === "string" ? toolCall._id : undefined,
      function: functionCall ?? {
        name: "",
        arguments: "",
      },
    };
  });
};

export const normalizeTraceResponseMessage = (
  value: unknown,
  templateFormat: TemplateFormat,
): AssistantMessage | null => {
  if (!isRecord(value)) {
    return null;
  }

  return {
    role: "assistant",
    template_format: templateFormat,
    content: normalizeMessageContent(value.content),
    tool_calls: normalizeToolCalls(value.tool_calls),
    function_call: normalizeFunctionCall(value.function_call),
    name: typeof value.name === "string" ? value.name : undefined,
    raw_request_display_role:
      typeof value.raw_request_display_role === "string"
        ? value.raw_request_display_role
        : undefined,
  };
};

const getDisplayToolCalls = (message: AssistantMessage): ToolCall[] | undefined => {
  if (message.tool_calls?.length) {
    return message.tool_calls;
  }

  if (!message.function_call) {
    return undefined;
  }

  return [
    {
      id: `function-call-${message.function_call.name || "tool"}`,
      type: "function",
      function: {
        name: message.function_call.name,
        arguments: message.function_call.arguments,
      },
    },
  ];
};

const getDisplayMessage = (message: Message): Message => {
  if (message.role !== "assistant") {
    return message;
  }

  const toolCalls = getDisplayToolCalls(message);
  if (!toolCalls) {
    return message;
  }

  return {
    ...message,
    tool_calls: toolCalls,
    function_call: undefined,
  };
};

export const buildTraceTranscriptBlocks = (messages: Message[]): TraceMessageBlock[] =>
  messages.map((message, index) => ({
    id: `message-${index}`,
    displayMessage: getDisplayMessage(message),
    jsonValue: message,
  }));

export const getTraceSystemMessage = (messages: Message[]): SystemMessage | undefined =>
  messages.find((message): message is SystemMessage => message.role === "system");

export const getTraceConversationMessages = (messages: Message[]): Message[] =>
  messages.filter(message => message.role !== "system");

/**
 * Read server-executed tool calls from provider usage (OpenRouter reports these separately).
 */
export const getServerToolCallCount = (response: unknown): number => {
  if (!isRecord(response)) {
    return 0;
  }
  const { usage } = response;
  if (!isRecord(usage)) {
    return 0;
  }
  const details = usage.server_tool_use_details;
  if (!isRecord(details)) {
    return 0;
  }
  const executed = details.tool_calls_executed;
  return typeof executed === "number" && Number.isFinite(executed) && executed > 0 ? executed : 0;
};

export const getTraceTranscriptStats = (
  blocks: TraceMessageBlock[],
  options?: { serverToolCallCount?: number },
): TraceTranscriptStats => {
  let turnCount = 0;
  // Client-executed tool calls (function calling) live on `message.tool_calls`.
  let messageToolCallCount = 0;
  // Server-side built-in tool calls reconstructed from transcript content.
  let contentBuiltInCount = 0;

  for (const block of blocks) {
    if (block.displayMessage.role === "assistant") {
      turnCount += 1;
      messageToolCallCount += block.displayMessage.tool_calls?.length ?? 0;
      contentBuiltInCount += countBuiltInToolCalls(block.displayMessage.content);
    }
  }

  // Use max(transcript count, usage count) to include server tools with no transcript trace.
  const serverToolCallCount = options?.serverToolCallCount ?? 0;

  return {
    turnCount,
    toolCallCount: messageToolCallCount + Math.max(contentBuiltInCount, serverToolCallCount),
  };
};
